import {
  Star,
  Heart,
  Rocket,
  Music,
  Book,
  Camera,
  Coffee,
  Gamepad2,
  Palette,
  Trophy,
  Plane,
  ShoppingCart,
  Sparkles,
  Sun,
  Moon,
  Cloud,
  Mountain,
  Flower2,
  Gift,
  Lightbulb,
  Flag,
  Award,
  Shield,
  Zap,
  Compass,
  Map as MapIcon,
  type LucideIcon
} from 'lucide-react'

/**
 * Icon Registry for Custom Life Spaces
 *
 * Provides a whitelist of allowed icons with O(1) bidirectional lookup
 * (name → component and component → name). Only these icons are imported,
 * allowing tree-shaking to remove the rest of lucide-react.
 *
 * @example
 * // Get icon component by name
 * const Icon = getLifeSpaceIcon('Rocket') // → Rocket component
 *
 * @example
 * // Get icon name from component
 * const name = getLifeSpaceIconName(Rocket) // → 'Rocket'
 */

const ICON_MAP: Record<string, LucideIcon> = {
  Star,
  Heart,
  Rocket,
  Music,
  Book,
  Camera,
  Coffee,
  Gamepad2,
  Palette,
  Trophy,
  Plane,
  ShoppingCart,
  Sparkles,
  Sun,
  Moon,
  Cloud,
  Mountain,
  Flower2,
  Gift,
  Lightbulb,
  Flag,
  Award,
  Shield,
  Zap,
  Compass,
  Map: MapIcon,
}

// Reverse map (Component → Name), built once at module load
const REVERSE_MAP = new Map<LucideIcon, string>()
for (const [name, component] of Object.entries(ICON_MAP)) {
  REVERSE_MAP.set(component, name)
}

/**
 * All icon names available in the icon picker UI.
 * Derived from the whitelist — currently 26 icons.
 */
export const AVAILABLE_ICON_NAMES = Object.keys(ICON_MAP)

/** Default fallback icon component (Star). */
export const DEFAULT_ICON = Star

/** Default fallback icon name. */
export const DEFAULT_ICON_NAME = 'Star'

/**
 * Resolves a Lucide icon name to its React component.
 * Returns `Star` as fallback for unknown or undefined names.
 *
 * @param name - Lucide icon name (e.g. `'Rocket'`, `'Heart'`)
 * @returns The matching `LucideIcon` component, or `Star` if not found
 *
 * @example
 * const Icon = getLifeSpaceIcon('Rocket')
 * return <Icon size={16} color="#6d28d9" />
 *
 * @example
 * // Unknown name falls back to Star
 * getLifeSpaceIcon('NonExistent') // → Star
 */
export function getLifeSpaceIcon(name: string | undefined): LucideIcon {
  if (!name) return DEFAULT_ICON
  return ICON_MAP[name] ?? DEFAULT_ICON
}

/**
 * Resolves a Lucide icon component back to its string name.
 * Returns `'Star'` as fallback for unknown or undefined components.
 *
 * @param component - A React component from the icon registry
 * @returns The icon name string, or `'Star'` if not found
 *
 * @example
 * import { Rocket } from 'lucide-react'
 * getLifeSpaceIconName(Rocket) // → 'Rocket'
 */
export function getLifeSpaceIconName(component: React.ComponentType<any> | undefined): string {
  if (!component) return DEFAULT_ICON_NAME
  return REVERSE_MAP.get(component as LucideIcon) ?? DEFAULT_ICON_NAME
}
